import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  IconButton,
  TablePagination,
  Tooltip,
  Fab,
} from "@mui/material";
import type { ChangeEvent, MouseEvent } from "react";
import {
  Close as CloseIcon,
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { getIcons, updateIcon, deleteIcon } from "../../../api";
import { useLocation } from "react-router-dom";
import IconSetting from "./IconSetting";
import IconAddiction from "./IconAddiction";
import PopoverDelete from "../../../components/PopoverDelete";
import PopoverRegister from "../../../components/PopoverRegister";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../components/SnackBarCustom";
import AliveSelect from "./AliveSelect";
import DamageSelect from "./DamageSelect";
import SalesSelect from "./SalesSelect";
import SaleStatusSelect from "./SaleStatusSecect";
import type {
  IconEditableColumn,
  IconItem,
  IconTableColumn,
} from "../../../types/icon";

const columns: IconTableColumn[] = [
  {
    id: "file_key",
    label: "アイコン",
    minWidth: "56px",
    align: "center",
    icon: true,
    isSmallScreen: true,
  },
  {
    id: "alive",
    label: "POS死活有無",
    minWidth: "80px",
    select: true,
    isSmallScreen: false,
  },
  {
    id: "sales",
    label: "POS営業有無",
    minWidth: "80px",
    select: true,
    isSmallScreen: false,
  },
  {
    id: "sale_status",
    label: "営業状況入力有無",
    minWidth: "80px",
    select: true,
    isSmallScreen: false,
  },
  {
    id: "damage",
    label: "被害状況有無",
    minWidth: "80px",
    select: true,
    isSmallScreen: false,
  },
  {
    id: "buttonSpace",
    label: "",
    minWidth: "80px",
    button: true,
    isSmallScreen: true,
  },
];

export default function IconTable() {
  const searchParams = new URLSearchParams(useLocation().search);
  const tenantId = searchParams.get("tenant_id");
  const tenantName = searchParams.get("name");

  const [_rowNum, setRowNum] = useState<number | "">("");
  const [selectedRowNum, isSelectedRowNum] = useState<number | "">("");
  const [registerPopoverOpen, setRegisterPopoverOpen] = useState(false);
  const registerButtonClick = (selectedRow: number) => {
    isSelectedRowNum(selectedRow);
    setRegisterPopoverOpen(true);
  };
  const registerPopupClose = () => {
    setRegisterPopoverOpen(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [icons, setIcons] = useState<IconItem[]>([]);
  const [totalData, setTotalData] = useState<number | undefined>();

  const getIconsData = async () => {
    try {
      const response = await getIcons({
        m_tenant_id: tenantId,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status === "ok") {
        const data = response.m_icons;
        const total = response.total_count;
        setIcons(data);
        setTotalData(total);
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const [imageData, setImageData] = useState<unknown>(null);

  useEffect(() => {
    getIconsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const postIconData = async (isSuccess: boolean) => {
    if (!isSuccess || selectedRowNum === "") {
      console.log("何も行いません");
      return;
    }

    const selectedIcon = icons[selectedRowNum];
    if (!selectedIcon) {
      return;
    }

    const imageUrl = `https://ljs-dic-public-resources-dev.s3.ap-northeast-1.amazonaws.com/${selectedIcon.file_key}`;
    const params = new FormData();
    params.append("id", String(selectedIcon.id));
    params.append("m_tenant_id", String(selectedIcon.m_tenant_id));
    params.append("icon_files", String(selectedIcon.icon_files ?? imageUrl));
    params.append("alive", String(selectedIcon.alive));
    params.append("sales", String(selectedIcon.sales));
    params.append("sale_status", String(selectedIcon.sale_status));
    params.append("damage", String(selectedIcon.damage));
    params.append("name", tenantName ?? "");
    console.log("更新入力データid", selectedIcon.id);
    console.log("更新入力データm_tenant_id", selectedIcon.m_tenant_id);
    console.log(
      "更新入力データicon_files",
      selectedIcon.icon_files || imageUrl
    );
    console.log(
      "更新入力データfile_key",
      `${selectedIcon.id}_${selectedIcon.alive}_${selectedIcon.sales}_${selectedIcon.sale_status}_${selectedIcon.damage}.png/svg`
    );
    console.log("更新入力データalive", selectedIcon.alive);
    console.log("更新入力データsales", selectedIcon.sales);
    console.log("更新入力データsale_status", selectedIcon.sale_status);
    console.log("更新入力データdamage", selectedIcon.damage);
    console.log("更新入力データname", tenantName);

    try {
      const status = await updateIcon(params, { id: selectedIcon.id });
      console.log("更新", status);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      getIconsData();
    } catch (error) {
      console.error("リクエストエラー:", error);
    }
  };

  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const deleteButtonClick = (iconId: number) => {
    setDeleteIconId(iconId);
    setDeletePopoverOpen(true);
  };
  const deletePopupClose = () => {
    setDeletePopoverOpen(false);
  };
  const [deleteIconId, setDeleteIconId] = useState<number | "">("");

  const handleDelete = async (isSuccess: boolean) => {
    if (!isSuccess || deleteIconId === "") {
      console.log("何も行いません");
      return;
    }

    try {
      const status = await deleteIcon(deleteIconId);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      console.log(`アイコン ${deleteIconId} を削除しました`);
      getIconsData();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  const handleChange = (
    inputValue: number | File | undefined,
    _iconId: number,
    selectedRow: number,
    column: IconEditableColumn | "icon_files"
  ) => {
    console.log("inputValue", inputValue);
    const updatedArray = [...icons];
    const updatedItem = { ...updatedArray[selectedRow] };
    if (column === "icon_files") {
      updatedItem.icon_files = inputValue as File | string;
    } else if (column === "alive") {
      updatedItem.alive = inputValue as number;
    } else if (column === "sales") {
      updatedItem.sales = inputValue as number;
    } else if (column === "sale_status") {
      updatedItem.sale_status = inputValue as number;
    } else if (column === "damage") {
      updatedItem.damage = inputValue as number;
    }
    updatedArray[selectedRow] = updatedItem;
    setRowNum(selectedRow);
    setIcons(updatedArray);
  };

  const handleLocationCodeClick = (selectedRow: number) => {
    isSelectedRowNum(selectedRow);
    setIsPopoverOpen(true);
  };

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleOpenAddDialog = (event: MouseEvent) => {
    event.stopPropagation();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = (event: MouseEvent) => {
    event.stopPropagation();
    setIsAddDialogOpen(false);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const buttonVaridate = (
    input: boolean | undefined,
    icon: IconItem,
    selectedRow: number
  ) =>
    input ? (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          justifyContent: "flex-end",
          ".edit-button": {
            display: "none",
          },
          "@media screen and (max-width:900px)": {
            ".submit-button": {
              display: "none",
            },
            ".edit-button": {
              display: "unset",
            },
          },
        }}
      >
        <Button
          variant="contained"
          className="submit-button"
          sx={{ pointerEvents: "auto" }}
          onClick={() => registerButtonClick(selectedRow)}
        >
          登録
        </Button>
        <Button
          variant="contained"
          className="edit-button"
          sx={{ pointerEvents: "auto" }}
          onClick={() => handleLocationCodeClick(selectedRow)}
        >
          編集
        </Button>
        <IconButton
          color="error"
          sx={{ pointerEvents: "auto" }}
          onClick={() => deleteButtonClick(icon.id)}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
      </Box>
    ) : null;

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const consoleCheck = () => {
    console.log("imageData", imageData);
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        {process.env.NODE_ENV === "development" && (
          <Box>
            <Button onClick={consoleCheck}>コンソール確認</Button>
          </Box>
        )}
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      borderBottom: 2,
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true ? "table-cell" : "none",
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {icons.map((icon, selectedRow) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={selectedRow}
                    sx={{
                      marginTop: "8px",
                      pointerEvents: "none",
                    }}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "8px",
                            backgroundColor:
                              column.id === "file_key"
                                ? "outline.main"
                                : "#FFF",
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          {column.select === true ? (
                            column.id === "damage" ? (
                              <DamageSelect
                                inputValue={icon.damage}
                                changedValue={(value) =>
                                  handleChange(
                                    value,
                                    icon.id,
                                    selectedRow,
                                    column.id as IconEditableColumn
                                  )
                                }
                              />
                            ) : column.id === "sale_status" ? (
                              <SaleStatusSelect
                                inputValue={icon.sale_status}
                                changedValue={(value) =>
                                  handleChange(
                                    value,
                                    icon.id,
                                    selectedRow,
                                    column.id as IconEditableColumn
                                  )
                                }
                              />
                            ) : column.id === "sales" ? (
                              <SalesSelect
                                inputValue={icon.sales}
                                changedValue={(value) =>
                                  handleChange(
                                    value,
                                    icon.id,
                                    selectedRow,
                                    column.id as IconEditableColumn
                                  )
                                }
                              />
                            ) : column.id === "alive" ? (
                              <AliveSelect
                                inputValue={icon.alive}
                                changedValue={(value) =>
                                  handleChange(
                                    value,
                                    icon.id,
                                    selectedRow,
                                    column.id as IconEditableColumn
                                  )
                                }
                              />
                            ) : null
                          ) : column.icon === true ? (
                            <IconSetting
                              input={icon}
                              column="file_key"
                              changedValue={(value) =>
                                handleChange(
                                  value,
                                  icon.id,
                                  selectedRow,
                                  "icon_files"
                                )
                              }
                            />
                          ) : null}
                          {buttonVaridate(column.button, icon, selectedRow)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <SnackbarCustom
          data={snackBarstatus}
          openStatus={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
        />
        <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              アイコン詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
            <TableBody>
              {columns.map((column) => {
                return (
                  <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                    <TableCell
                      sx={{
                        color: "outline.dark",
                        fontWeight: "bold",
                        boxSizing: "border-box",
                        width: "calc((100vw - 64px) / 2)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          WebkitWritingMode: "horizontal-tb",
                          height: "100%",
                          padding: "8px",
                        }}
                      >
                        {column.label}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        boxSizing: "border-box",
                        width: "calc((100vw - 64px) / 2)",
                        backgroundColor:
                          column.id === "file_key" ? "outline.main" : "#FFF",
                        overflowWrap: "break-word",
                        ".edit-button": {
                          display: "none",
                        },
                        ".submit-button": {
                          display: "unset",
                        },
                      }}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Dialog>
        <Box
          sx={{ display: "flex", justifyContent: "center", margin: "16px 0" }}
        >
          <Tooltip title="アイコン追加登録">
            <Fab
              color="primary"
              size="small"
              onClick={handleOpenAddDialog}
              sx={{ zIndex: 399 }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </Box>
        <PopoverDelete
          open={deletePopoverOpen}
          onClose={deletePopupClose}
          yesNo={handleDelete}
        />

        <IconAddiction
          open={isAddDialogOpen}
          onClose={handleCloseAddDialog}
          columns={columns}
          tenantId={tenantId}
        />
        <PopoverRegister
          open={registerPopoverOpen}
          onClose={registerPopupClose}
          yesNo={(isSuccess) => postIconData(isSuccess)}
        />

        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={totalData ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
              margin: "0 !important",
              padding: "0 !important",
            },
          }}
        />
      </Paper>
    </>
  );
}
